import React, { useEffect, useState, useRef, useCallback } from 'react';
import { GridSectionToolbar } from './GridSectionToolbar';
import { AssetFooter } from './AssetFooter';
import { VersionSelector } from './VersionSelector';
import { RectangleOverlay } from './RectangleOverlay';
import { AssetContextMenu } from './AssetContextMenu';
import { FloatingActionButtonsWithCollection } from './FloatingActionButtonsWithCollection';
import { DrawingTool, ImageVersion, AspectRatioOption, ViewModeOption } from './types';

interface GridAssetPanelProps {
  aspectRatio: string;
  viewMode: string;
  header: string;
  subheader: string;
  companyName: string;
  companyLogo?: string;
  primaryImage: string;
  secondaryImage: string;
  primaryImages?: ImageVersion[];
  secondaryImages?: ImageVersion[];
  selectedPrimaryVersion?: string;
  selectedSecondaryVersion?: string;
  onPrimaryVersionChange?: (version: string) => void;
  onSecondaryVersionChange?: (version: string) => void;
  imagePositions: Record<number, { x: number; y: number }>;
  imageZooms: Record<number, number>;
  showHeader?: boolean;
  primaryImageDate?: string; // YYYYMMDD format
  secondaryImageDate?: string; // YYYYMMDD format
  activeTool?: DrawingTool;
  onToolChange?: (tool: DrawingTool) => void;
  onGridImageZoomChange?: (zooms: Record<string, number>) => void;
  onGridImagePositionChange?: (positions: Record<string, { x: number; y: number }>) => void;
  isLinked?: boolean;
  onLinkStateChange?: (isLinked: boolean) => void;
  onRectangleChange?: (rectangles: Record<string, Array<{ x: number; y: number; width: number; height: number; color?: string }>>) => void;
  selectedRectangle?: { imageId: string; rectIndex: number } | null;
  onSelectRectangle?: (imageId: string, rectIndex: number) => void;
  onDeleteRectangle?: () => void;
  onResetImage?: (imageId: string) => void;
  rectangles?: Record<string, Array<{ x: number; y: number; width: number; height: number; color?: string }>>;
  // Mouse event handlers for pan tool
  onMouseDown?: (e: React.MouseEvent, imageIndex: number) => void;
  onMouseMove?: (e: React.MouseEvent) => void;
  onMouseUp?: () => void;
  onMouseLeave?: () => void;
  // Context menu props
  aspectRatios?: AspectRatioOption[];
  availableViewModes?: ViewModeOption[];
  onAspectRatioChange?: (ratio: string) => void;
  onViewModeChange?: (mode: string) => void;
  // Header/subheader change callbacks
  onHeaderChange?: (header: string) => void;
  onSubheaderChange?: (subheader: string) => void;
  // Floating action buttons props
  showSaveControls?: boolean;
  onSave?: (collectionId?: string) => void;
  onCancel?: () => void;
  enableCollectionChange?: boolean;
  initialCollectionId?: string;
  onCollectionChange?: (collectionId: string) => void;
  // Image URLs for context menu links
  imageUrls?: {
    primaryOriginal?: string;
    primaryCropped?: string;
    primaryMarkup?: string;
    secondaryOriginal?: string;
    secondaryCropped?: string;
    secondaryMarkup?: string;
  };
}

// Enhanced image container for grid layout with drawing support
const DrawableImageContainer: React.FC<{
  imageUrl: string;
  position: { x: number; y: number };
  zoom: number;
  containerClass: string;
  activeTool: DrawingTool;
  rectangles: Array<{ x: number; y: number; width: number; height: number; color?: string }>;
  onRectangleAdd?: (rect: { x: number; y: number; width: number; height: number }) => void;
  onMouseDown?: (e: React.MouseEvent, imageId: string) => void;
  onMouseMove?: (e: React.MouseEvent) => void;
  onMouseUp?: () => void;
  onMouseLeave?: () => void;
  onWheel?: (e: React.WheelEvent, imageId: string) => void;
  imageId: string;
  selectedRectangle?: { imageId: string; rectIndex: number } | null;
  onSelectRectangle?: (imageId: string, rectIndex: number) => void;
  onDeleteRectangle?: () => void;
  onClearRectangles?: (imageId: string) => void;
  onRectangleMove?: (imageId: string, rectangles: Array<{ x: number; y: number; width: number; height: number; color?: string }>) => void;
}> = ({ imageUrl, position, zoom, containerClass, activeTool, rectangles, onRectangleAdd, onMouseDown, onMouseMove, onMouseUp, onMouseLeave, onWheel, imageId, selectedRectangle, onSelectRectangle, onDeleteRectangle, onClearRectangles, onRectangleMove }) => {
  const [isDrawing, setIsDrawing] = useState(false);
  const [startPoint, setStartPoint] = useState<{ x: number; y: number } | null>(null);
  const [currentPoint, setCurrentPoint] = useState<{ x: number; y: number } | null>(null);

  const handleMouseDown = (e: React.MouseEvent) => {

    if (activeTool === 'rectangle') {
      const rect = e.currentTarget.getBoundingClientRect();
      const clientX = e.clientX - rect.left;
      const clientY = e.clientY - rect.top;

      // Store raw client coordinates like panning does
      setStartPoint({ x: clientX, y: clientY });
      setCurrentPoint({ x: clientX, y: clientY });
      setIsDrawing(true);
      e.preventDefault();
    } else if (activeTool === 'pan' && onMouseDown) {
      onMouseDown(e, imageId);
    }
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    // For pan tool, delegate to parent handler first
    if (activeTool === 'pan' && onMouseMove) {
      onMouseMove(e);
      return;
    }

    // For rectangle tool, handle locally
    if (isDrawing && startPoint && activeTool === 'rectangle') {
      const rect = e.currentTarget.getBoundingClientRect();
      const clientX = e.clientX - rect.left;
      const clientY = e.clientY - rect.top;

      // Store raw client coordinates like panning does
      setCurrentPoint({ x: clientX, y: clientY });
    }
  };

  const handleMouseUp = () => {
    // For pan tool, delegate to parent handler first
    if (activeTool === 'pan' && onMouseUp) {
      onMouseUp();
      return;
    }

    // For rectangle tool, handle locally
    if (isDrawing && startPoint && currentPoint && activeTool === 'rectangle') {
      const width = Math.abs(currentPoint.x - startPoint.x);
      const height = Math.abs(currentPoint.y - startPoint.y);

      if (width > 5 && height > 5) {
        const newRect = {
          x: Math.min(startPoint.x, currentPoint.x),
          y: Math.min(startPoint.y, currentPoint.y),
          width,
          height,
          color: '#84cc16'
        };

        onRectangleAdd?.(newRect);
      }
    }
    setIsDrawing(false);
    setStartPoint(null);
    setCurrentPoint(null);
  };

  const handleMouseLeave = () => {
    // For pan tool, delegate to parent handler first
    if (activeTool === 'pan' && onMouseLeave) {
      onMouseLeave();
      return;
    }

    // For rectangle tool, cancel current drawing
    if (isDrawing && activeTool === 'rectangle') {
      setIsDrawing(false);
      setStartPoint(null);
      setCurrentPoint(null);
    }
  };

  return (
    <div 
      className={`relative overflow-hidden rounded min-h-[80px] border border-gray-400 ${containerClass}`}
      data-image-id={imageId}
      onMouseDown={handleMouseDown}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      onMouseLeave={handleMouseLeave}
      onWheel={onWheel ? (e) => onWheel(e, imageId) : undefined}
      style={{ 
        cursor: activeTool === 'rectangle' ? 'crosshair' : activeTool === 'pan' ? 'grab' : 'default' 
      }}
    >
      {imageUrl && (
        <>
          <img
            src={imageUrl}
            alt="Asset preview"
            className="absolute top-0 left-0 pointer-events-none"
            style={{
              width: `${100 * zoom}%`,
              height: 'auto',
              minWidth: '100%',
              minHeight: '100%',
              objectFit: 'cover',
              transform: `translate(${position.x || 0}px, ${position.y || 0}px) scale(${zoom})`,
              transformOrigin: 'top left'
            }}
            draggable={false}
          />
          
          <RectangleOverlay
            rectangles={rectangles}
            selectedRectangle={selectedRectangle?.imageId === imageId ? { imageIndex: 0, rectIndex: selectedRectangle.rectIndex } : null}
            imageIndex={0}
            onSelect={(_, rectIndex) => onSelectRectangle?.(imageId, rectIndex)}
            onDelete={() => {
              onClearRectangles?.(imageId);
              onDeleteRectangle?.();
            }}
            onMove={(rectIndex, newX, newY) => {
              const newRects = [...rectangles];
              if (newRects[rectIndex]) {
                newRects[rectIndex] = { ...newRects[rectIndex], x: newX, y: newY };
                onRectangleMove?.(imageId, newRects);
              }
            }}
            position={position}
            zoom={zoom}
          />
          
          {/* Current drawing preview */}
          {isDrawing && startPoint && currentPoint && (
            <div
              className="absolute pointer-events-none border-2 bg-lime-500/20 border-dashed"
              style={{
                borderColor: '#84cc16',
                left: `${Math.min(startPoint.x, currentPoint.x)}px`,
                top: `${Math.min(startPoint.y, currentPoint.y)}px`,
                width: `${Math.abs(currentPoint.x - startPoint.x)}px`,
                height: `${Math.abs(currentPoint.y - startPoint.y)}px`,
                zIndex: 10
              }}
            />
          )}
        </>
      )}
    </div>
  );
};

export const GridAssetPanel: React.FC<GridAssetPanelProps> = ({
  aspectRatio,
  viewMode,
  header,
  subheader,
  companyName,
  companyLogo,
  primaryImage,
  secondaryImage,
  primaryImages = [],
  secondaryImages = [],
  selectedPrimaryVersion,
  selectedSecondaryVersion,
  onPrimaryVersionChange,
  onSecondaryVersionChange,
  imagePositions,
  imageZooms,
  showHeader = true,
  primaryImageDate = "20250702",
  secondaryImageDate = "20250815",
  activeTool = 'pan',
  onToolChange,
  onGridImageZoomChange,
  onGridImagePositionChange,
  isLinked: propIsLinked = false,
  onLinkStateChange,
  onRectangleChange,
  selectedRectangle,
  onSelectRectangle,
  onDeleteRectangle,
  onResetImage,
  rectangles = {},
  onMouseDown,
  onMouseMove,
  onMouseUp,
  aspectRatios = [],
  availableViewModes = [],
  onAspectRatioChange,
  onViewModeChange,
  onHeaderChange,
  onSubheaderChange,
  showSaveControls = false,
  onSave,
  onCancel,
  imageUrls,
  enableCollectionChange,
  initialCollectionId,
  onCollectionChange
}) => {
  const canvasRef = useRef<HTMLDivElement>(null);
  const [assetSize, setAssetSize] = useState({ width: 0, height: 0 });
  
  // Drawing state for each image
  const [imageRectangles, setImageRectangles] = useState<Record<string, Array<{ x: number; y: number; width: number; height: number; color?: string }>>>({});

  // Initialize rectangles from parent props
  React.useEffect(() => {
    if (rectangles && Object.keys(rectangles).length > 0) {
      setImageRectangles(rectangles);
    }
  }, [rectangles]);

  // Sync rectangles with parent state
  const prevImageRectangles = React.useRef<Record<string, Array<{ x: number; y: number; width: number; height: number; color?: string }>>>(imageRectangles);
  
  React.useEffect(() => {
    const currentRectanglesStr = JSON.stringify(imageRectangles);
    const prevRectanglesStr = JSON.stringify(prevImageRectangles.current);
    
    if (onRectangleChange && currentRectanglesStr !== prevRectanglesStr) {
      prevImageRectangles.current = imageRectangles;
      onRectangleChange(imageRectangles);
    }
  }, [imageRectangles, onRectangleChange]);
  
  // Editable text state - initialized with props
  const [editableHeader, setEditableHeader] = useState(header);
  const [editableSubheader, setEditableSubheader] = useState(subheader);
  const [isEditingHeader, setIsEditingHeader] = useState(false);
  const [isEditingSubheader, setIsEditingSubheader] = useState(false);

  // Sync editable state with props when they change
  useEffect(() => {
    setEditableHeader(header);
    setEditableSubheader(subheader);
  }, [header, subheader]);

  // Use imageInteraction state as single source of truth
  const currentPositions = {
    primary1: imagePositions[0] || { x: 0, y: 0 },
    secondary1: imagePositions[1] || { x: 0, y: 0 },
    primary2: imagePositions[0] || { x: 0, y: 0 },
    secondary2: imagePositions[1] || { x: 0, y: 0 },
    primary3: { x: 0, y: 0 },
  };
  
  const currentZooms = {
    primary1: imageZooms[0] || 1,
    secondary1: imageZooms[1] || 1,
    primary2: imageZooms[0] || 1,
    secondary2: imageZooms[1] || 1,
    primary3: 1,
  };

  // Bridge callbacks to update parent imageInteraction state directly
  const updateImageInteractionZoom = useCallback((imageKey: string, zoom: number) => {
    onGridImageZoomChange?.({ [imageKey]: zoom });
  }, [onGridImageZoomChange]);

  const updateImageInteractionPosition = useCallback((imageKey: string, position: { x: number; y: number }) => {
    onGridImagePositionChange?.({ [imageKey]: position });
  }, [onGridImagePositionChange]);

  // Date formatting function
  const formatDate = (dateStr: string): string => {
    if (!dateStr || dateStr.length !== 8) return dateStr;
    
    const year = dateStr.substring(0, 4);
    const month = dateStr.substring(4, 6);
    const day = dateStr.substring(6, 8);
    
    const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
    
    const monthNames = [
      "January", "February", "March", "April", "May", "June",
      "July", "August", "September", "October", "November", "December"
    ];
    
    const dayWithSuffix = (day: number): string => {
      if (day >= 11 && day <= 13) return `${day}th`;
      switch (day % 10) {
        case 1: return `${day}st`;
        case 2: return `${day}nd`;
        case 3: return `${day}rd`;
        default: return `${day}th`;
      }
    };
    
    return `${monthNames[date.getMonth()]} ${dayWithSuffix(date.getDate())}, ${date.getFullYear()}`;
  };

  // Version selector handlers
  const handlePrimaryVersionChange = (version: string) => {
    if (onPrimaryVersionChange) {
      onPrimaryVersionChange(version);
    }
  };

  const handleSecondaryVersionChange = (version: string) => {
    if (onSecondaryVersionChange) {
      onSecondaryVersionChange(version);
    }
  };

  // Drag state for image panning
  const [dragState, setDragState] = useState<{
    isDragging: boolean;
    imageId: string | null;
    startX: number;
    startY: number;
    initialX: number;
    initialY: number;
  }>({
    isDragging: false,
    imageId: null,
    startX: 0,
    startY: 0,
    initialX: 0,
    initialY: 0
  });

  // Link state for synchronized zoom/pan - use prop value or check if initial values differ
  const [isLinked, setIsLinked] = useState(() => {
    // Use prop value if provided
    if (propIsLinked !== undefined) {
      return propIsLinked;
    }

    // Otherwise, check if initial values differ to determine default state
    if (viewMode !== 'single') {
      const zoom0 = imageZooms[0];
      const zoom1 = imageZooms[1];
      const pos0 = imagePositions[0];
      const pos1 = imagePositions[1];

      // Check for significant differences (threshold for floating point comparison)
      const threshold = 0.1;

      if (zoom0 && zoom1 && (Math.abs(zoom0 - zoom1) > threshold)) {
        return false;
      }

      if (pos0 && pos1 && (
        Math.abs(pos0.x - pos1.x) > threshold ||
        Math.abs(pos0.y - pos1.y) > threshold
      )) {
        return false;
      }
    }

    return true;
  });

  // Sync local state with prop changes
  useEffect(() => {
    if (propIsLinked !== undefined && propIsLinked !== isLinked) {
      setIsLinked(propIsLinked);
    }
  }, [propIsLinked, isLinked]);

  // Unified mouse handler that connects parent handlers with local drag functionality
  const handleUnifiedMouseDown = useCallback((e: React.MouseEvent, imageId: string) => {

    // If parent has provided mouse handlers, use those (for compatibility with VisualAssetEditor)
    if (onMouseDown) {
      // Convert imageId to imageIndex for parent handler
      const imageIndex = imageId.includes('primary') ? 0 : 1;
      onMouseDown(e, imageIndex);
      return;
    }

    // Otherwise, use local drag functionality
    handleLocalMouseDown(e, imageId);
  }, [onMouseDown]);

  // Local mouse event handlers - update parent directly
  const handleLocalMouseDown = useCallback((e: React.MouseEvent, imageId: string) => {
    const currentPos = currentPositions[imageId] || { x: 0, y: 0 };
    
    setDragState({
      isDragging: true,
      imageId,
      startX: e.clientX,
      startY: e.clientY,
      initialX: currentPos.x,
      initialY: currentPos.y
    });
  }, [currentPositions]);

  // Unified mouse move handler
  const handleUnifiedMouseMove = useCallback((e: React.MouseEvent) => {
    if (onMouseMove) {
      onMouseMove(e);
      return;
    }
    // Convert React.MouseEvent to MouseEvent for local handler
    handleLocalMouseMove(e.nativeEvent);
  }, [onMouseMove]);

  const handleLocalMouseMove = useCallback((e: MouseEvent) => {
    if (!dragState.isDragging || !dragState.imageId) return;

    const deltaX = e.clientX - dragState.startX;
    const deltaY = e.clientY - dragState.startY;
    
    const newPosition = {
      x: dragState.initialX + deltaX,
      y: dragState.initialY + deltaY
    };

    if (isLinked && viewMode !== 'single') {
      const linkedImageId = getLinkedImageId(dragState.imageId);
      updateImageInteractionPosition(dragState.imageId, newPosition);
      if (linkedImageId) {
        updateImageInteractionPosition(linkedImageId, newPosition);
      }
    } else {
      updateImageInteractionPosition(dragState.imageId, newPosition);
    }
  }, [dragState, isLinked, viewMode, updateImageInteractionPosition]);

  // Unified mouse up handler
  const handleUnifiedMouseUp = useCallback(() => {
    if (onMouseUp) {
      onMouseUp();
      return;
    }
    handleLocalMouseUp();
  }, [onMouseUp]);

  const handleLocalMouseUp = useCallback(() => {
    setDragState({
      isDragging: false,
      imageId: null,
      startX: 0,
      startY: 0,
      initialX: 0,
      initialY: 0
    });
  }, []);

  // Zoom handlers - update parent directly
  const handleZoomIn = useCallback((imageId: string) => {
    const currentZoom = currentZooms[imageId];
    const newZoom = Math.min(currentZoom * 1.2, 5);
    
    if (isLinked && viewMode !== 'single') {
      const linkedImageId = getLinkedImageId(imageId);
      updateImageInteractionZoom(imageId, newZoom);
      if (linkedImageId) {
        updateImageInteractionZoom(linkedImageId, newZoom);
      }
    } else {
      updateImageInteractionZoom(imageId, newZoom);
    }
  }, [currentZooms, isLinked, viewMode, updateImageInteractionZoom]);

  const handleZoomOut = useCallback((imageId: string) => {
    const currentZoom = currentZooms[imageId];
    const newZoom = Math.max(currentZoom / 1.2, 0.2);
    
    if (isLinked && viewMode !== 'single') {
      const linkedImageId = getLinkedImageId(imageId);
      updateImageInteractionZoom(imageId, newZoom);
      if (linkedImageId) {
        updateImageInteractionZoom(linkedImageId, newZoom);
      }
    } else {
      updateImageInteractionZoom(imageId, newZoom);
    }
  }, [currentZooms, isLinked, viewMode, updateImageInteractionZoom]);

  const handleZoomChange = useCallback((imageId: string, zoom: number) => {
    if (isLinked && viewMode !== 'single') {
      const linkedImageId = getLinkedImageId(imageId);
      updateImageInteractionZoom(imageId, zoom);
      if (linkedImageId) {
        updateImageInteractionZoom(linkedImageId, zoom);
      }
    } else {
      updateImageInteractionZoom(imageId, zoom);
    }
  }, [isLinked, viewMode, updateImageInteractionZoom]);

  const handleResetPosition = useCallback((imageId: string) => {
    updateImageInteractionPosition(imageId, { x: 0, y: 0 });
    updateImageInteractionZoom(imageId, 1);
    // Clear rectangles for this image
    setImageRectangles(prev => ({
      ...prev,
      [imageId]: []
    }));
    onResetImage?.(imageId);
  }, [updateImageInteractionPosition, updateImageInteractionZoom, onResetImage]);

  // Mouse wheel zoom handler
  const handleWheelZoom = useCallback((e: React.WheelEvent, imageId: string) => {
    e.preventDefault();
    e.stopPropagation();
    
    const zoomSensitivity = 0.05;
    const zoomDirection = e.deltaY > 0 ? -1 : 1;
    const zoomChange = zoomDirection * zoomSensitivity;
    const currentZoom = currentZooms[imageId];
    const newZoom = Math.min(Math.max(currentZoom + zoomChange, 0.2), 5);
    
    if (isLinked && viewMode !== 'single') {
      const linkedImageId = getLinkedImageId(imageId);
      updateImageInteractionZoom(imageId, newZoom);
      if (linkedImageId) {
        updateImageInteractionZoom(linkedImageId, newZoom);
      }
    } else {
      updateImageInteractionZoom(imageId, newZoom);
    }
  }, [currentZooms, isLinked, viewMode, updateImageInteractionZoom]);

  // Helper function to get linked image ID
  const getLinkedImageId = (imageId: string): string | null => {
    const linkPairs: Record<string, string> = {
      'primary1': 'secondary1',
      'secondary1': 'primary1',
      'primary2': 'secondary2', 
      'secondary2': 'primary2'
    };
    return linkPairs[imageId] || null;
  };

  // Link toggle handler
  const handleLinkToggle = useCallback((sourceImageId: string) => {
    if (!isLinked && viewMode !== 'single') {
      // When turning link on, sync the other image to match this one
      const linkedImageId = getLinkedImageId(sourceImageId);
      if (linkedImageId) {
        const sourceZoom = currentZooms[sourceImageId];
        const sourcePosition = currentPositions[sourceImageId];
        
        updateImageInteractionZoom(linkedImageId, sourceZoom);
        updateImageInteractionPosition(linkedImageId, sourcePosition);
      }
    }
    const newLinkedState = !isLinked;
    setIsLinked(newLinkedState);
    onLinkStateChange?.(newLinkedState);
  }, [isLinked, viewMode, currentZooms, currentPositions, updateImageInteractionZoom, updateImageInteractionPosition, onLinkStateChange]);

  // Convert aspect ratio string to number
  const getAspectRatioValue = () => {
    switch (aspectRatio) {
      case '1:1': return 1;
      case '16:9': return 16/9;
      case '4:3': return 4/3;
      default: return 1;
    }
  };

  const resizeAsset = () => {
    if (!canvasRef.current) return;

    // Get available space from parent container
    const parent = canvasRef.current.parentElement;
    if (!parent) {
      // Fallback to fixed size if no parent
      const aspect = getAspectRatioValue();
      const baseSize = 600;
      let targetW = baseSize, targetH = baseSize;
      if (aspect > 1) {
        targetH = baseSize / aspect;
      } else if (aspect < 1) {
        targetW = baseSize * aspect;
      }
      setAssetSize({ width: targetW, height: targetH });
      return;
    }

    const parentW = parent.clientWidth;
    const parentH = parent.clientHeight;
    const aspect = getAspectRatioValue();
    const marginPct = 0.05; // 5% margin on each side
    const marginW = parentW * marginPct * 2; // Total horizontal margin (both sides)
    const marginH = parentH * marginPct * 2; // Total vertical margin (both sides)

    const availableW = parentW - marginW;
    const availableH = parentH - marginH;

    let targetW: number, targetH: number;

    // Calculate size to fit within available space while maintaining aspect ratio
    if (aspect === 1) {
      const size = Math.min(availableW, availableH);
      targetW = size;
      targetH = size;
    } else {
      // Try fitting by width
      targetW = availableW;
      targetH = availableW / aspect;

      // If height is too large, fit by height instead
      if (targetH > availableH) {
        targetH = availableH;
        targetW = availableH * aspect;
      }
    }

    // Ensure minimum size
    const minSize = 400;
    if (targetW < minSize || targetH < minSize) {
      if (aspect >= 1) {
        targetW = minSize;
        targetH = minSize / aspect;
      } else {
        targetH = minSize;
        targetW = minSize * aspect;
      }
    }

    setAssetSize({ width: targetW, height: targetH });
  };

  useEffect(() => {
    if (dragState.isDragging) {
      window.addEventListener('mousemove', handleLocalMouseMove);
      window.addEventListener('mouseup', handleLocalMouseUp);
      document.body.style.cursor = 'grabbing';
      document.body.style.userSelect = 'none';
    }

    return () => {
      window.removeEventListener('mousemove', handleLocalMouseMove);
      window.removeEventListener('mouseup', handleLocalMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
  }, [dragState.isDragging, handleLocalMouseMove, handleLocalMouseUp]);

  useEffect(() => {
    resizeAsset();
    
    const handleResize = () => resizeAsset();
    window.addEventListener('resize', handleResize);
    
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [aspectRatio]);

  return (
    <div
      ref={canvasRef}
      className="relative bg-white shadow-xl rounded-xl border-2 border-gray-300 overflow-hidden"
      data-image-container
      style={{
        width: `${assetSize.width}px`,
        height: `${assetSize.height}px`,
        display: 'grid',
        gridTemplateRows: showHeader ? '100px 1fr 75px' : '1fr 75px'
      }}
    >
          {/* Context Menu */}
          {aspectRatios.length > 0 && availableViewModes.length > 0 && onAspectRatioChange && onViewModeChange && (
            <AssetContextMenu
              aspectRatio={aspectRatio}
              viewMode={viewMode}
              aspectRatios={aspectRatios}
              availableViewModes={availableViewModes}
              onAspectRatioChange={onAspectRatioChange}
              onViewModeChange={onViewModeChange}
              imageUrls={imageUrls}
            />
          )}
        {/* Header Section - Conditionally rendered */}
        {showHeader && (
          <div className="bg-white flex flex-col items-center justify-center px-4 py-2 border-b border-gray-300">
            {/* Editable Header */}
            {isEditingHeader ? (
              <input
                type="text"
                value={editableHeader}
                onChange={(e) => {
                  setEditableHeader(e.target.value);
                  onHeaderChange?.(e.target.value);
                }}
                onBlur={() => setIsEditingHeader(false)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    setIsEditingHeader(false);
                  }
                }}
                className="text-lg font-bold text-center bg-transparent border-none outline-none w-full"
                autoFocus
              />
            ) : (
              <h1 
                onClick={() => setIsEditingHeader(true)}
                className="text-lg font-bold text-center cursor-pointer hover:bg-gray-50 px-2 py-1 rounded w-full"
              >
                {editableHeader || "Click to edit header"}
              </h1>
            )}
            
            {/* Editable Subheader */}
            {isEditingSubheader ? (
              <input
                type="text"
                value={editableSubheader}
                onChange={(e) => {
                  setEditableSubheader(e.target.value);
                  onSubheaderChange?.(e.target.value);
                }}
                onBlur={() => setIsEditingSubheader(false)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    setIsEditingSubheader(false);
                  }
                }}
                className="text-sm text-center bg-transparent border-none outline-none w-full mt-1"
                autoFocus
              />
            ) : (
              <p 
                onClick={() => setIsEditingSubheader(true)}
                className="text-sm text-center cursor-pointer hover:bg-gray-50 px-2 py-1 rounded w-full mt-1"
              >
                {editableSubheader || "Click to edit subheader"}
              </p>
            )}
          </div>
        )}
        
        {/* Body Section - Adjusts size based on header visibility */}
        <div className="border-b border-gray-300" style={{
          display: 'grid',
          gridTemplateColumns: viewMode === 'side_by_side' ? '1fr 1fr' : '1fr',
          gridTemplateRows: viewMode === 'stacked' ? '50px 1fr 40px 50px 1fr 40px' : viewMode === 'side_by_side' ? '50px 1fr 40px' : '50px 1fr 40px'
        }}>
          {viewMode === 'side_by_side' ? (
            <>
              {/* Left Side - Primary Image Toolbar */}
              <div className="bg-gray-100 dark:bg-gray-800 flex items-center justify-center text-xs border-r border-b border-gray-300 shadow-sm">
                <GridSectionToolbar
                  imageId="primary1"
                  zoom={currentZooms.primary1}
                  activeTool={activeTool}
                  onZoomIn={() => handleZoomIn('primary1')}
                  onZoomOut={() => handleZoomOut('primary1')}
                  onZoomChange={(zoom) => handleZoomChange('primary1', zoom)}
                  onResetPosition={() => handleResetPosition('primary1')}
                  onToolChange={onToolChange}
                  showLinkControl={true}
                  isLinked={isLinked}
                  onLinkToggle={() => handleLinkToggle('primary1')}
                />
              </div>
              {/* Right Side - Secondary Image Toolbar */}
              <div className="bg-gray-100 dark:bg-gray-800 flex items-center justify-center text-xs border-b border-gray-300 shadow-sm">
                <GridSectionToolbar
                  imageId="secondary1"
                  zoom={currentZooms.secondary1}
                  activeTool={activeTool}
                  onZoomIn={() => handleZoomIn('secondary1')}
                  onZoomOut={() => handleZoomOut('secondary1')}
                  onZoomChange={(zoom) => handleZoomChange('secondary1', zoom)}
                  onResetPosition={() => handleResetPosition('secondary1')}
                  onToolChange={onToolChange}
                  showLinkControl={true}
                  isLinked={isLinked}
                  onLinkToggle={() => handleLinkToggle('secondary1')}
                />
              </div>
              {/* Left Side - Primary Image */}
              <div className="bg-white flex flex-col text-sm p-2 min-h-0 h-full border-r border-gray-300">
                <DrawableImageContainer
                  imageUrl={primaryImage || ""}
                  position={currentPositions.primary1}
                  zoom={currentZooms.primary1}
                  containerClass="w-full h-full cursor-grab active:cursor-grabbing"
                  activeTool={activeTool}
                  rectangles={imageRectangles.primary1 || []}
                  onRectangleAdd={(rect) => {
                    const newRectangles = { ...imageRectangles, primary1: [rect] };
                    setImageRectangles(newRectangles);
                    onRectangleChange?.(newRectangles);
                  }}
                  onMouseDown={handleUnifiedMouseDown}
                  onMouseMove={handleUnifiedMouseMove}
                  onMouseUp={handleUnifiedMouseUp}
                  onWheel={handleWheelZoom}
                  imageId="primary1"
                  selectedRectangle={selectedRectangle}
                  onSelectRectangle={onSelectRectangle}
                  onDeleteRectangle={onDeleteRectangle}
                  onClearRectangles={(imageId) => {
                    setImageRectangles(prev => ({ ...prev, [imageId]: [] }));
                  }}
                  onRectangleMove={(imageId, rects) => {
                    setImageRectangles(prev => ({ ...prev, [imageId]: rects }));
                  }}
                />
              </div>
              {/* Right Side - Secondary Image */}
              <div className="bg-white flex flex-col text-sm p-2 min-h-0 h-full">
                <DrawableImageContainer
                  imageUrl={secondaryImage || ""}
                  position={currentPositions.secondary1}
                  zoom={currentZooms.secondary1}
                  containerClass="w-full h-full cursor-grab active:cursor-grabbing"
                  activeTool={activeTool}
                  rectangles={imageRectangles.secondary1 || []}
                  onRectangleAdd={(rect) => {
                    const newRectangles = { ...imageRectangles, secondary1: [rect] };
                    setImageRectangles(newRectangles);
                    onRectangleChange?.(newRectangles);
                  }}
                  onMouseDown={handleUnifiedMouseDown}
                  onMouseMove={handleUnifiedMouseMove}
                  onMouseUp={handleUnifiedMouseUp}
                  onWheel={handleWheelZoom}
                  imageId="secondary1"
                  selectedRectangle={selectedRectangle}
                  onSelectRectangle={onSelectRectangle}
                  onDeleteRectangle={onDeleteRectangle}
                  onClearRectangles={(imageId) => {
                    setImageRectangles(prev => ({ ...prev, [imageId]: [] }));
                  }}
                  onRectangleMove={(imageId, rects) => {
                    setImageRectangles(prev => ({ ...prev, [imageId]: rects }));
                  }}
                />
              </div>
              {/* Left Side - Primary Version Selector */}
              <div className="bg-white flex items-center justify-center text-xs font-medium px-2 border-r border-gray-300 h-10 min-h-[2.5rem]">
                <VersionSelector
                  versions={primaryImages}
                  selectedVersion={selectedPrimaryVersion || primaryImages[0]?.version || primaryImageDate}
                  onVersionChange={onPrimaryVersionChange || (() => {})}
                />
              </div>
              {/* Right Side - Secondary Version Selector */}
              <div className="bg-white flex items-center justify-center text-xs font-medium px-2 h-10 min-h-[2.5rem]">
                <VersionSelector
                  versions={secondaryImages}
                  selectedVersion={selectedSecondaryVersion || secondaryImages[0]?.version || secondaryImageDate}
                  onVersionChange={onSecondaryVersionChange || (() => {})}
                />
              </div>
            </>
          ) : viewMode === 'stacked' ? (
            <>
              {/* Stacked Layout - 6 rows */}
              <div className="bg-gray-100 dark:bg-gray-800 flex items-center justify-center text-xs border-b border-gray-300 shadow-sm">
                <GridSectionToolbar
                  imageId="primary2"
                  zoom={currentZooms.primary2}
                  activeTool={activeTool}
                  onZoomIn={() => handleZoomIn('primary2')}
                  onZoomOut={() => handleZoomOut('primary2')}
                  onZoomChange={(zoom) => handleZoomChange('primary2', zoom)}
                  onResetPosition={() => handleResetPosition('primary2')}
                  onToolChange={onToolChange}
                  showLinkControl={true}
                  isLinked={isLinked}
                  onLinkToggle={() => handleLinkToggle('primary2')}
                />
              </div>
              <div className="bg-white flex flex-col text-sm p-2 min-h-0 h-full">
                <DrawableImageContainer
                  imageUrl={primaryImage || ""}
                  position={currentPositions.primary2}
                  zoom={currentZooms.primary2}
                  containerClass="w-full h-full cursor-grab active:cursor-grabbing"
                  activeTool={activeTool}
                  rectangles={imageRectangles.primary2 || []}
                  onRectangleAdd={(rect) => {
                    const newRectangles = { ...imageRectangles, primary2: [rect] };
                    setImageRectangles(newRectangles);
                    onRectangleChange?.(newRectangles);
                  }}
                  onMouseDown={handleUnifiedMouseDown}
                  onMouseMove={handleUnifiedMouseMove}
                  onMouseUp={handleUnifiedMouseUp}
                  onWheel={handleWheelZoom}
                  imageId="primary2"
                  selectedRectangle={selectedRectangle}
                  onSelectRectangle={onSelectRectangle}
                  onDeleteRectangle={onDeleteRectangle}
                  onClearRectangles={(imageId) => {
                    setImageRectangles(prev => ({ ...prev, [imageId]: [] }));
                  }}
                  onRectangleMove={(imageId, rects) => {
                    setImageRectangles(prev => ({ ...prev, [imageId]: rects }));
                  }}
                />
              </div>
              <div className="bg-white flex items-center justify-center text-xs font-medium px-2 h-10 min-h-[2.5rem]">
                <VersionSelector
                  versions={primaryImages}
                  selectedVersion={selectedPrimaryVersion || primaryImages[0]?.version || primaryImageDate}
                  onVersionChange={onPrimaryVersionChange || (() => {})}
                />
              </div>
              <div className="bg-gray-100 dark:bg-gray-800 flex items-center justify-center text-xs border-b border-gray-300 shadow-sm">
                <GridSectionToolbar
                  imageId="secondary2"
                  zoom={currentZooms.secondary2}
                  activeTool={activeTool}
                  onZoomIn={() => handleZoomIn('secondary2')}
                  onZoomOut={() => handleZoomOut('secondary2')}
                  onZoomChange={(zoom) => handleZoomChange('secondary2', zoom)}
                  onResetPosition={() => handleResetPosition('secondary2')}
                  onToolChange={onToolChange}
                  showLinkControl={true}
                  isLinked={isLinked}
                  onLinkToggle={() => handleLinkToggle('secondary2')}
                />
              </div>
              <div className="bg-white flex flex-col text-sm p-2 min-h-0 h-full">
                <DrawableImageContainer
                  imageUrl={secondaryImage || ""}
                  position={currentPositions.secondary2}
                  zoom={currentZooms.secondary2}
                  containerClass="w-full h-full cursor-grab active:cursor-grabbing"
                  activeTool={activeTool}
                  rectangles={imageRectangles.secondary2 || []}
                  onRectangleAdd={(rect) => {
                    const newRectangles = { ...imageRectangles, secondary2: [rect] };
                    setImageRectangles(newRectangles);
                    onRectangleChange?.(newRectangles);
                  }}
                  onMouseDown={handleUnifiedMouseDown}
                  onMouseMove={handleUnifiedMouseMove}
                  onMouseUp={handleUnifiedMouseUp}
                  onWheel={handleWheelZoom}
                  imageId="secondary2"
                  selectedRectangle={selectedRectangle}
                  onSelectRectangle={onSelectRectangle}
                  onDeleteRectangle={onDeleteRectangle}
                  onClearRectangles={(imageId) => {
                    setImageRectangles(prev => ({ ...prev, [imageId]: [] }));
                  }}
                  onRectangleMove={(imageId, rects) => {
                    setImageRectangles(prev => ({ ...prev, [imageId]: rects }));
                  }}
                />
              </div>
              <div className="bg-white flex items-center justify-center text-xs font-medium px-2 h-10 min-h-[2.5rem]">
                <VersionSelector
                  versions={secondaryImages}
                  selectedVersion={selectedSecondaryVersion || secondaryImages[0]?.version || secondaryImageDate}
                  onVersionChange={onSecondaryVersionChange || (() => {})}
                />
              </div>
            </>
          ) : (
            // Single view mode - single image with toolbar
            <>
              <div className="bg-gray-100 dark:bg-gray-800 flex items-center justify-center text-xs border-b border-gray-300 shadow-sm">
                <GridSectionToolbar
                  imageId="primary3"
                  zoom={currentZooms.primary3}
                  activeTool={activeTool}
                  onZoomIn={() => handleZoomIn('primary3')}
                  onZoomOut={() => handleZoomOut('primary3')}
                  onZoomChange={(zoom) => handleZoomChange('primary3', zoom)}
                  onResetPosition={() => handleResetPosition('primary3')}
                  onToolChange={onToolChange}
                  showLinkControl={false}
                  isLinked={false}
                />
              </div>
              <div className="bg-white flex flex-col text-sm p-2 min-h-0 h-full">
                <DrawableImageContainer
                  imageUrl={primaryImage || ""}
                  position={currentPositions.primary3}
                  zoom={currentZooms.primary3}
                  containerClass="w-full h-full cursor-grab active:cursor-grabbing"
                  activeTool={activeTool}
                  rectangles={imageRectangles.primary3 || []}
                  onRectangleAdd={(rect) => {
                    const newRectangles = { ...imageRectangles, primary3: [rect] };
                    setImageRectangles(newRectangles);
                    onRectangleChange?.(newRectangles);
                  }}
                  onMouseDown={handleUnifiedMouseDown}
                  onMouseMove={handleUnifiedMouseMove}
                  onMouseUp={handleUnifiedMouseUp}
                  onWheel={handleWheelZoom}
                  imageId="primary3"
                  selectedRectangle={selectedRectangle}
                  onSelectRectangle={onSelectRectangle}
                  onDeleteRectangle={onDeleteRectangle}
                  onClearRectangles={(imageId) => {
                    setImageRectangles(prev => ({ ...prev, [imageId]: [] }));
                  }}
                  onRectangleMove={(imageId, rects) => {
                    setImageRectangles(prev => ({ ...prev, [imageId]: rects }));
                  }}
                />
              </div>
              <div className="bg-white flex items-center justify-center text-xs font-medium px-2 h-10 min-h-[2.5rem]">
                <VersionSelector
                  versions={primaryImages}
                  selectedVersion={selectedPrimaryVersion || primaryImages[0]?.version || primaryImageDate}
                  onVersionChange={onPrimaryVersionChange || (() => {})}
                />
              </div>
            </>
          )}
        </div>

        {/* Footer Section */}
        <AssetFooter
          companyName={companyName}
          viewMode={viewMode}
          aspectRatio={aspectRatio}
          companyLogo={companyLogo}
        />

          {/* Floating Action Buttons */}
          <FloatingActionButtonsWithCollection
            showSaveControls={showSaveControls}
            onSave={onSave}
            onCancel={onCancel}
            initialCollectionId={initialCollectionId}
            onCollectionChange={onCollectionChange}
          />
    </div>
  );
};